import { createSignal, For, type JSX } from 'solid-js';
import { type TabItem, TabList, tabId, tabPanelId } from './TabList';

// A tab rail and its panels, one visible (`<TabList/>` owns the rail and its
// keyboard contract). Tabs whose "panels" are routes are `<LinkTabs/>` instead.
//
// Every panel stays MOUNTED and is toggled with `hidden` rather than swapped.
// That is a contract, not an optimisation: the enroll body reveals a one-time
// token whose seat slot is burnt the moment it is minted, and unmounting its
// panel to peek at the other tab would throw the token away. `hidden` also keeps
// the panel out of the dialog trap's Tab order (`lib/dialog-trap.ts` skips
// anything under `[hidden]`).

export type TabsItem = TabItem & {
  readonly panel: () => JSX.Element;
};

export type TabsProps = {
  /** The rail's accessible name, already translated. */
  readonly label: string;
  /** Namespaces the tab and panel ids — two tab sets on one page must not collide. */
  readonly idPrefix: string;
  /** Non-empty; the first item starts selected. */
  readonly items: readonly TabsItem[];
};

export function Tabs(props: TabsProps): JSX.Element {
  const first = props.items[0];
  if (first === undefined) throw new Error('<Tabs/> needs at least one item');
  const [selected, setSelected] = createSignal(first.id);

  return (
    <div class="tabs">
      <TabList
        label={props.label}
        idPrefix={props.idPrefix}
        items={props.items}
        selected={selected()}
        onSelect={setSelected}
      />
      <div class="tabs__panel">
        <For each={props.items}>
          {(item) => (
            <div
              id={tabPanelId(props.idPrefix, item.id)}
              role="tabpanel"
              aria-labelledby={tabId(props.idPrefix, item.id)}
              hidden={selected() !== item.id}
            >
              {item.panel()}
            </div>
          )}
        </For>
      </div>
    </div>
  );
}
